import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Универсальный layout-шаблон для CRM-страниц.
 *
 * Использование:
 *   <CrmPage
 *     title="Заявки"
 *     onRefresh={() => reload()}
 *     stats={[
 *       { label: 'Всего', value: 14, accent: 'neutral' },
 *       { label: 'Ожидают', value: 14, accent: 'warning' },
 *       ...
 *     ]}
 *     filters={<>...input + pills...</>}
 *     bulkBar={selected.length > 0 && <BulkBar ... />}
 *     sidebar={<Sidebar />}
 *   >
 *     ...table-card content
 *   </CrmPage>
 */
export default function CrmPage({
  sidebarSlot,
  containerClass = 'applications-basic-container',
  sidebarClass = 'applications-sidebar-container',
  contentClass = 'applications-basic-content',
  title,
  actions,
  stats,
  filters,
  bulkBar,
  children,
}) {
  return (
    <div className={`${containerClass} crmpage`}>
      {sidebarSlot && (
        <div className={sidebarClass}>{sidebarSlot}</div>
      )}
      <div className={`${contentClass} crmpage__main`}>
        <header className="crmpage__head">
          <h1 className="crmpage__title">{title}</h1>
          {actions && <div className="crmpage__head-actions">{actions}</div>}
        </header>

        {stats && stats.length > 0 && (
          <section className="crmpage__stats">
            {stats.map((s, i) => (
              <StatCard key={i} {...s} />
            ))}
            {actions === undefined && (
              // если actions не переданы, refresh-кнопка ставится в конец stats
              null
            )}
          </section>
        )}

        {filters && (
          <section className="crmpage__filters">{filters}</section>
        )}

        {children}

        {bulkBar}
      </div>
    </div>
  );
}

/* ---------- StatCard ---------- */
export function StatCard({ label, value, accent = 'neutral', wide }) {
  return (
    <div className={`crmpage__stat crmpage__stat--${accent} ${wide ? 'crmpage__stat--wide' : ''}`}>
      <span className="crmpage__stat-label">{label}</span>
      <span className="crmpage__stat-value">{value}</span>
    </div>
  );
}

/* ---------- Refresh — квадратная кнопка-иконка ---------- */
export function RefreshButton({ onClick }) {
  return (
    <button
      type="button"
      className="crmpage__refresh"
      onClick={onClick}
      title="Обновить"
      aria-label="Обновить"
    ><RefreshIcon sx={{ fontSize: 20 }} /></button>
  );
}

/* ---------- FilterPill ---------- */
export function FilterPill({ active, onClick, count, children }) {
  return (
    <button
      type="button"
      className={`crmpage__pill ${active ? 'is-active' : ''}`}
      onClick={onClick}
    >
      <span>{children}</span>
      {typeof count === 'number' && (
        <span className={`crmpage__pill-count ${active ? 'is-active' : ''}`}>{count}</span>
      )}
    </button>
  );
}

/* ---------- Search input ---------- */
export function SearchField({ value, onChange, placeholder }) {
  return (
    <div className="crmpage__search">
      <svg className="crmpage__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input type="text" placeholder={placeholder} value={value} onChange={onChange} />
    </div>
  );
}

/* ---------- StatusBadge — семантический pill ---------- */
export function StatusBadge({ name, tone: forcedTone }) {
  const n = String(name || '').toLowerCase();
  let tone = forcedTone || 'neutral';
  if (!forcedTone) {
    if (/ожидает|новая|новый|формируется/.test(n))             tone = 'warning';
    else if (/подтверждена|подтверждён|готов|оплачен|доставлен|закрыт|завершен/.test(n)) tone = 'success';
    else if (/отмен|возврат|отклон/.test(n))                   tone = 'danger';
    else if (/в пути|производ|собира|в работе|на доставке/.test(n)) tone = 'info';
  }
  return <span className={`crmpage__status crmpage__status--${tone}`}>{name || '—'}</span>;
}

/* ---------- Table card обёртка ---------- */
export function TableCard({ children }) {
  return <section className="crmpage__table-card">{children}</section>;
}

/* ---------- States: loading / empty ---------- */
export function LoadingState({ text = 'Загрузка…' }) {
  return (
    <div className="crmpage__state">
      <div className="crmpage__spinner" />
      <p>{text}</p>
    </div>
  );
}
export function EmptyState({ title = 'Пусто', text }) {
  return (
    <div className="crmpage__state">
      <p className="crmpage__state-title">{title}</p>
      {text && <p className="crmpage__state-text">{text}</p>}
    </div>
  );
}

/* ---------- Pagination ---------- */
export function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  const pages = Array.from({ length: total }, (_, i) => i + 1)
    .slice(Math.max(0, current - 3), Math.max(0, current - 3) + 5);
  return (
    <div className="crmpage__pagination">
      <button type="button" className="crmpage__page-nav"
        disabled={current === 1}
        onClick={() => onChange(Math.max(1, current - 1))}
      >Назад</button>
      {pages.map((n) => (
        <button key={n} type="button"
          className={`crmpage__page-btn ${current === n ? 'is-active' : ''}`}
          onClick={() => onChange(n)}
        >{n}</button>
      ))}
      <button type="button" className="crmpage__page-nav"
        disabled={current === total}
        onClick={() => onChange(Math.min(total, current + 1))}
      >Вперёд</button>
    </div>
  );
}

/* ---------- BulkBar ---------- */
export function BulkBar({ count, onClear, children }) {
  if (count === 0) return null;
  return (
    <div className="crmpage__bulk">
      <div className="crmpage__bulk-info">
        <span className="crmpage__bulk-count">{count}</span>
        <span>{pluralize(count, ['запись выбрана', 'записи выбрано', 'записей выбрано'])}</span>
        <button type="button" className="crmpage__bulk-deselect" onClick={onClear}>Снять выделение</button>
      </div>
      <div className="crmpage__bulk-actions">{children}</div>
    </div>
  );
}

/* ---------- Утилиты ---------- */
export function pluralize(n, [one, few, many]) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5)   return few;
  if (b === 1)          return one;
  return many;
}
